import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {ColumnDef} from "@tanstack/react-table";
import dayjs from "dayjs";
import {DataTable} from "@/lib/components/DataTable";
import {RequestDetailDTO} from "@/api/data-contracts";
import {CommonI18N, ReportI18N} from "@/translations";
import {DateFormatter} from "@/lib/components/DateFormate";

interface RequestsDetailReportProps {
    data: RequestDetailDTO[];
}

interface RequestsDetailRow extends RequestDetailDTO {
    id: number;
}

/**
 * Report table showing detail rows for all requests in the selected date range.
 */
const RequestsDetailReport: React.FC<RequestsDetailReportProps> = ({ data }) => {
    const { t } = useTranslation();
    const dateFormat = DateFormatter("date_hm");

    const rows = useMemo<RequestsDetailRow[]>(
        () => data.map((item, index) => ({ ...item, id: index })),
        [data]
    );

    const columns = useMemo<ColumnDef<RequestsDetailRow>[]>(
        () => [
            {
                header: t(ReportI18N.page.column.company),
                accessorFn: (row) => row.companyName ?? "",
                id: "companyName"
            },
            {
                header: t(ReportI18N.page.column.companyNumber),
                accessorFn: (row) => row.companyId ?? "",
                id: "companyId"
            },
            {
                header: t(ReportI18N.page.column.status),
                accessorFn: (row) => (row.status ? t(CommonI18N.enum.requestStatus[row.status]) : ""),
                id: "status"
            },
            {
                header: t(ReportI18N.page.column.publishedAt),
                accessorFn: (row) => (row.publishedAt ? dayjs(row.publishedAt).format(dateFormat) : ""),
                id: "publishedAt"
            },
            {
                header: t(ReportI18N.page.column.referenceNumber),
                accessorFn: (row) => row.customerReferenceNumber ?? "–",
                id: "customerReferenceNumber"
            }
        ],
        [t, dateFormat]
    );

    return <DataTable columns={columns} data={rows} noResultsValue="" striped maxHeight={940} />;
};

export default RequestsDetailReport;
